import {ChangeDetectionStrategy, Component, inject} from '@angular/core';

import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from 'src/environments/environment';
import {map, switchMap} from 'rxjs';
import {DateTime} from 'luxon';
import {LoadComponent} from '@brunata-angular/ui';
import {z} from 'zod';
import {mapParseItem} from '@brunata-angular/util';
import {CookieOptions, CookieService} from 'ngx-cookie-service';
import {AuthService} from '@brunata-angular/auth';

@Component({
    selector: 'app-webbill-login',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadComponent],
    template: ` <app-load />`,
})
export class WebbillLoginComponent {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly http = inject(HttpClient);
    private readonly cookieService = inject(CookieService);
    private readonly authService = inject(AuthService);

    constructor() {
        this.route.queryParamMap
            .pipe(
                takeUntilDestroyed(),
                map(params => {
                    const authSubject = params.get('auth_subject');
                    const sessionId = params.get('webbill_sessionid');
                    const consumerId = params.get('consumer_id');
                    const route = params.get('route');
                    if (authSubject == null) {
                        throw new Error('Required query parameter "auth_subject" not found');
                    }
                    if (sessionId == null) {
                        throw new Error('Required query parameter "webbill_sessionid" not found');
                    }
                    const formData = new URLSearchParams();
                    formData.append('auth_subject', authSubject);
                    formData.append('webbill_sessionid', sessionId);
                    if (consumerId !== null) {
                        formData.append('consumer_id', consumerId);
                    }
                    return {formData, route};
                }),
                switchMap(({formData, route}) => {
                    const options = {
                        headers: new HttpHeaders().set(
                            'Content-Type',
                            'application/x-www-form-urlencoded',
                        ),
                    };
                    return this.http
                        .post(environment.oauthUrl + '/oauth/tokenfromwebbill', formData, options)
                        .pipe(
                            mapParseItem(tokenSchema),
                            map(response => ({response, route})),
                        );
                }),
            )
            .subscribe(({response, route}) => {
                // Drops an earlier Keycloak login from the shared cookie jar, so its refresh token is
                // never replayed as this session.
                this.authService.logOut();
                const cookieOptions: CookieOptions = {
                    path: '/',
                    secure: true,
                    sameSite: 'Strict',
                };
                this.cookieService.set('access_token', response.access_token, cookieOptions);
                // Refresh access token 60 seconds before it runs out just to be safe.
                this.cookieService.set(
                    'access_token_expires',
                    DateTime.now()
                        .plus({seconds: response.expires_in - 60})
                        .toISO(),
                    cookieOptions,
                );
                void this.router.navigateByUrl(route ?? '/');
            });
    }
}

const tokenSchema = z.object({
    /* eslint-disable @typescript-eslint/naming-convention */
    access_token: z.string(),
    expires_in: z.number(),
    /* eslint-enable @typescript-eslint/naming-convention */
});
